import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionDetails, AccordionSummary, Stack, styled, Tooltip } from 'shell/components';
import Card from 'shell/components/Card';
import Text from 'shell/components/Text';
import { FileTypes } from 'utils/constants';
import { indexToLetters } from 'utils/helper';

const ACCEPTED_ORIGINAL_TYPES = [
    // Word
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',

    // PowerPoint
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

const getViewerCategory = (fileType, originalFile) => {
    if (fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        return FileTypes.SHEET;
    }
    if (
        fileType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
        fileType === 'application/vnd.ms-powerpoint'
    ) {
        return FileTypes.PRESENTATION;
    }
    if (fileType === 'application/pdf' && (!originalFile || ACCEPTED_ORIGINAL_TYPES.includes(originalFile?.type))) {
        return FileTypes.PDF;
    }
    return null;
};

const SupportingEvidence = ({ locations, fileNames = [], fileType, originalFile, sx = { p: 3 } }) => {
    const { t } = useTranslation('assignment');

    const [expanded, setExpanded] = useState(false);
    const [unavailableIndex, setUnavailableIndex] = useState(null);
    const timerRef = useRef(null);

    const viewerCategory = getViewerCategory(fileType, originalFile);
    const isClickable = viewerCategory !== FileTypes.SHEET;
    // TODO: fix file names once multifile is merged
    const parsedFileNames = fileNames?.map((f) => f.name);
    const locationsExist = locations?.length > 0;

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const handleAccordionChange = (_, isExpanded) => {
        setExpanded(isExpanded);
    };

    const handleLocationClick = useCallback(
        (index) => {
            if (!isClickable) return;

            const el = document.querySelector(`mark[data-highlight-index="${index}"]`);

            if (el) {
                setUnavailableIndex(null);
                if (timerRef.current) clearTimeout(timerRef.current);

                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
                setUnavailableIndex(index);

                if (timerRef.current) clearTimeout(timerRef.current);
                timerRef.current = setTimeout(() => setUnavailableIndex(null), 2000);
            }
        },
        [isClickable]
    );

    const renderText = (location) => {
        switch (viewerCategory) {
            case FileTypes.SHEET: {
                const { col, row } = location.value || {};
                const isInvalid = typeof col !== 'number' || typeof row !== 'number';
                if (isInvalid) return location.value;
                return t('grading-section.view-in-cell', 'View in Cell {{cell}}', {
                    cell: `${indexToLetters(col)}${row + 1}`,
                });
            }
            case FileTypes.PRESENTATION:
                return t('grading-section.view-in-slide', 'View in Slide {{slide}}', { slide: location.value + 1 });
            default:
                return location.value;
        }
    };

    if (!viewerCategory) return null;

    return (
        <Stack sx={sx} spacing={2}>
            <Stack spacing={2}>
                <Stack spacing={2} direction={'row'} alignItems={'center'}>
                    <Text>{t('grading-section.supporting-evidence-label', 'Supporting Evidence')}</Text>
                    {locationsExist && (
                        <ItemTextTag variant={'p3'} weight={'medium'}>
                            {t('item-count', '{{count}} items', { count: locations?.length })}
                        </ItemTextTag>
                    )}
                </Stack>
            </Stack>
            <Stack direction={'row'} alignItems={'center'} spacing={2} justifyContent={'space-between'} width={'100%'}>
                <SupportingEvidenceAccordion
                    expanded={locationsExist ? expanded : false}
                    onChange={handleAccordionChange}
                    disabled={!locationsExist}
                    onClick={(e) => e.stopPropagation()}
                >
                    <SupportingEvidenceAccordionSummary>
                        <Text color={'inherit'}>
                            {expanded
                                ? t('grading-section.hide-locations-label', 'Hide Locations')
                                : t('grading-section.view-locations-label', 'View Locations')}
                        </Text>
                    </SupportingEvidenceAccordionSummary>
                    <SupportingEvidenceAccordionDetails>
                        {locationsExist &&
                            locations.map((location, index) => {
                                const sheetName = location.sheet_name;

                                return (
                                    <Tooltip
                                        key={index}
                                        open={unavailableIndex === index}
                                        title={t('grading-section.highlight-unavailable', 'Highlight unavailable')}
                                        arrow
                                    >
                                        <CustomCard
                                            onClick={() => handleLocationClick(index)}
                                            isClickable={isClickable}
                                        >
                                            {(parsedFileNames[index] || sheetName) && (
                                                <Stack spacing={2} direction={'row'} alignItems={'center'}>
                                                    {[parsedFileNames[index], sheetName].map(
                                                        (text, idx) =>
                                                            text && (
                                                                <LocationTag key={idx} variant={'p3'} weight={'medium'}>
                                                                    {text}
                                                                </LocationTag>
                                                            )
                                                    )}
                                                </Stack>
                                            )}
                                            <Text variant={'p3'}>{renderText(location)}</Text>
                                        </CustomCard>
                                    </Tooltip>
                                );
                            })}
                    </SupportingEvidenceAccordionDetails>
                </SupportingEvidenceAccordion>
                {!locationsExist && (
                    <StatusText>{t('grading-section.location-unavailable', 'Location unavailable')}</StatusText>
                )}
            </Stack>
        </Stack>
    );
};

const CustomCard = styled(Card, {
    shouldForwardProp: (prop) => prop !== 'isClickable',
})(({ theme, isClickable }) => {
    return {
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(2.5),
        boxShadow: 'none',
        cursor: isClickable ? 'pointer' : 'default',

        '&:hover': {
            borderColor: !isClickable ? theme.palette.ensightful.neutral[300] : theme.palette.ensightful.primary[500],
            margin: !isClickable ? 'initial' : '-1px',
            borderWidth: !isClickable ? '1px' : '2px',
        },
    };
});

const ItemTextTag = styled(Text)(({ theme }) => {
    return {
        padding: theme.spacing(1, 2),
        backgroundColor: theme.palette.ensightful.primary[50],
        color: theme.palette.ensightful.primary[700],
        borderRadius: '40px',
    };
});

const LocationTag = styled(Text)(({ theme }) => {
    return {
        padding: theme.spacing(1, 2),
        backgroundColor: theme.palette.ensightful.neutral[100],
        borderRadius: '40px',
    };
});

const StatusText = styled(Text)(({ theme }) => {
    return {
        width: '100%',
        textAlign: 'end',
        color: theme.palette.ensightful.error[600],
    };
});

const SupportingEvidenceAccordion = styled(Accordion)(({ theme }) => {
    return {
        width: '100%',
        background: 'inherit',

        '&.Mui-disabled': {
            backgroundColor: theme.palette.ensightful.shades[0],

            '.MuiAccordionSummary-root div': {
                color: theme.palette.ensightful.neutral[400],
            },
        },
    };
});

const SupportingEvidenceAccordionSummary = styled(AccordionSummary)(({ theme }) => {
    return {
        width: 'fit-content',
        '& > div': {
            color: theme.palette.ensightful.primary[500],
        },
    };
});

const SupportingEvidenceAccordionDetails = styled(AccordionDetails)(({ theme }) => {
    return {
        marginTop: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    };
});

export default SupportingEvidence;
